/**
 * Hook for managing user presence status
 */

import { useState, useCallback } from 'react';
import type { PresenceStatus } from '../../types';

export interface UsePresenceStatusOptions {
  initialStatus?: PresenceStatus;
  onStatusChange?: (status: PresenceStatus) => void;
}

export interface UsePresenceStatusReturn {
  status: PresenceStatus;
  setStatus: (status: PresenceStatus) => void;
  isOnline: boolean;
}

/**
 * Hook for managing user presence status
 *
 * @example
 * ```tsx
 * const { status, setStatus, isOnline } = usePresenceStatus({
 *   initialStatus: 'Online',
 *   onStatusChange: (status) => console.log('Status changed:', status),
 * });
 *
 * <PresenceIndicator status={status} />
 * <Button onClick={() => setStatus('Away')}>Set Away</Button>
 * ```
 */
export function usePresenceStatus(
  options: UsePresenceStatusOptions = {}
): UsePresenceStatusReturn {
  const { initialStatus = 'Offline', onStatusChange } = options;

  const [status, setStatusState] = useState<PresenceStatus>(initialStatus);

  const setStatus = useCallback(
    (newStatus: PresenceStatus) => {
      setStatusState(newStatus);
      onStatusChange?.(newStatus);

      // In real implementation, this would call SDK:
      // const presenceClient = usePresence();
      // presenceClient.updateStatus(newStatus);
    },
    [onStatusChange]
  );

  return {
    status,
    setStatus,
    isOnline: status === 'Online' || status === 'Busy',
  };
}
